<template>
  <Loading :isLoading="loadingMessages" id="chat-session">
    <div class="chat-messages" @scrollend="userScrolled">
      <chat-message :message="message" v-for="message in displayedMessages" :key="messageKey(message)" />
      <chat-spinner></chat-spinner>
      <div id="bottom" ref="bottomOfMessages"></div>
    </div>
    <div class="notification-bar">
      <div class="ws-status" v-if="wsConnectionState !== 'connected'" :class="wsConnectionState">
        {{ wsStatusLabel }}
      </div>
      <div class="new-messages" v-if="newMessages > 0">{{ newMessages }} New Messages</div>
    </div>
    <div class="chat-controls">
      <ChatInput @user-message-submitted="resumeAutoScrollToBottom"></ChatInput>
    </div>
  </Loading>
</template>

<script>
import ChatMessage from "@/components/chat/ChatMessage"
import ChatSpinner from '@/components/chat/ChatSpinner'
import ChatInput from '@/components/chat/ChatInput'
import Loading from "@/components/shared/Loading"
import { mapState } from 'vuex'
export default {
  name: "ChatSession",
  components: {
    ChatMessage,
    ChatSpinner,
    ChatInput,
    Loading
  },
  data() {
    return {
      autoScroll: true,
      newMessages: 0,
      autoScrollInProgress: false,
      userMessageInputText: "",
      userCreating: false,
      systemCreating: true,
      wsConnectionState: "connected",
      _wsReconnectInFlight: false,
    }
  },
  computed: {
    ...mapState({
      messages: state => state.chat.messages.all,
      session: state => state.chat.currentSession,
      loadingSession: state => state.chat.loading.session,
      loadingMessages: state => state.chat.loading.messages,
      // userCreating: state => state.chat.userCreating,
      // systemCreating: state => state.chat.systemCreating
    }),
    userMessages() {
      return this.messages.filter((message) => !message.author.isBot)
    },
    sessionId() {
      return this.$route.params.sessionId
    },
    displayedMessages() {
      const filtered = (this.messages || []).filter((m) => {
        const latest = m.history && Array.isArray(m.history) && m.history.length > 0 ? m.history.slice(-1)[0] : null
        const latestStatus = latest && typeof latest === "object" ? latest.status : null
        return latestStatus !== "cancelled" && latestStatus !== "cancelling"
      })
      return filtered
    },
    processingCount() {
      return (this.messages || []).filter((m) => {
        if (!m.author || !m.author.isBot || m.text !== "Processing...") return false
        const latest = m.history && Array.isArray(m.history) && m.history.length > 0 ? m.history.slice(-1)[0] : null
        const latestStatus = latest && typeof latest === "object" ? latest.status : null
        return latestStatus !== "cancelled" && latestStatus !== "cancelling"
      }).length
    },
    // Identity of the message list only — status/history updates must not change this.
    messageIdsKey() {
      return (this.displayedMessages || []).map(function (m) { return String(m.id) }).join(",")
    },
    wsStatusLabel() {
      if (this.wsConnectionState === "connecting") {
        return "Reconnecting live updates…"
      }
      if (this.wsConnectionState === "disconnected") {
        return "Live updates disconnected — reconnecting…"
      }
      return ""
    }
  },
  async mounted() {
    this._onGraphqlWsConnected = (event) => {
      const detail = event && event.detail ? event.detail : {}
      this.wsConnectionState = "connected"
      if (detail.wasRetry) {
        this.reconnectChatLiveUpdates()
      }
    }
    this._onGraphqlWsClosed = () => {
      this.wsConnectionState = "disconnected"
    }
    this._onGraphqlWsConnecting = (event) => {
      const detail = event && event.detail ? event.detail : {}
      if (detail.isRetry || this.wsConnectionState === "disconnected") {
        this.wsConnectionState = "connecting"
      }
    }
    if (typeof window !== "undefined" && window.addEventListener) {
      window.addEventListener("cb-graphql-ws-connected", this._onGraphqlWsConnected)
      window.addEventListener("cb-graphql-ws-closed", this._onGraphqlWsClosed)
      window.addEventListener("cb-graphql-ws-connecting", this._onGraphqlWsConnecting)
    }
    const id = this.$route.params.sessionId
    if (id != null && id !== undefined && id !== "") {
      this.loadSession(parseInt(id, 10))
    }
  },
  beforeRouteUpdate(to, from, next) {
    next()
  },
  async destroyed() {
    if (typeof window !== "undefined" && window.removeEventListener) {
      if (this._onGraphqlWsConnected) {
        window.removeEventListener("cb-graphql-ws-connected", this._onGraphqlWsConnected)
      }
      if (this._onGraphqlWsClosed) {
        window.removeEventListener("cb-graphql-ws-closed", this._onGraphqlWsClosed)
      }
      if (this._onGraphqlWsConnecting) {
        window.removeEventListener("cb-graphql-ws-connecting", this._onGraphqlWsConnecting)
      }
    }
    const session = this.session
    const sessionId = session && session.id != null ? session.id : this.sessionId
    console.log("Unsubscribing from session", sessionId)
    if (sessionId != null && sessionId !== undefined && sessionId !== "") {
      await this.$store.dispatch("unsubscribeFromChatSession", sessionId)
      await this.$store.dispatch("unsubscribeFromChatMessages", sessionId)
    }
  },
  watch: {
    async sessionId(newVal, oldVal) {
      console.log("[Chat] sessionId changed", { from: oldVal, to: newVal })
      if (oldVal != null && oldVal !== undefined && oldVal !== "") {
        await this.$store.dispatch("unsubscribeFromChatSession", oldVal)
        await this.$store.dispatch("unsubscribeFromChatMessages", oldVal)
      }
      if (newVal != null && newVal !== undefined && newVal !== "") {
        this.loadSession(newVal)
      }
    },
    processingCount(newVal, oldVal) {
      // Final bot reply replaced "Processing..." — scroll only if user is already at bottom.
      if (oldVal === 1 && newVal === 0) {
        this.scroll()
      }
    },
    // Scroll on new/removed messages only — ignore status updates on existing rows.
    messageIdsKey(newVal, oldVal) {
      if (oldVal === undefined || newVal === oldVal) {
        return
      }
      this.scroll()
    },
  },
  methods: {
    messageKey(message) {
      if (message && message.__pending && message.clientId) {
        return `pending-${message.clientId}`
      }
      return `message-${message.id}`
    },
    usePreviousMessage() {
      this.userMessageInputText = this.userMessages[this.userMessages.length - 1].text
    },
    async loadSession(id) {
      const sessionId = parseInt(id, 10)
      console.log("[Chat] loadSession", { id, sessionId })
      await this.$store.dispatch("getChatSession", sessionId)
      await this.$store.dispatch("getChatMessagesForSession", sessionId)
      // Subscribe to session first so SESSION_UPDATED can trigger message refetch before message subscription is used
      const chatSessionSubscription = await this.$store.dispatch("subscribeToChatSession", sessionId)
      if (chatSessionSubscription) {
        console.debug("Subscribed to Chat Session")
      }
      const chatMessageSubscription = await this.$store.dispatch("subscribeToChatMessages", sessionId)
      if (chatMessageSubscription) {
        console.debug("Subscribed to Chat Messages")
      }
      this.$store.dispatch("setPageTitle",`Chat - ${this.session.title}`
      );

      window.setTimeout(() => this.scrollToBottom(), 100)
    },
    async reconnectChatLiveUpdates() {
      const rawId = this.sessionId
      if (rawId == null || rawId === undefined || rawId === "") {
        return
      }
      if (this._wsReconnectInFlight) {
        return
      }
      this._wsReconnectInFlight = true
      try {
        const sessionId = parseInt(rawId, 10)
        console.log("[Chat] reconnectChatLiveUpdates after websocket reconnect", { sessionId: sessionId })
        await this.$store.dispatch("reconnectChatSubscriptions", sessionId)
      } finally {
        this._wsReconnectInFlight = false
      }
    },
    userScrolled(e) {
      this.autoScroll = Math.abs(e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight) < 10
      if (this.autoScroll) {
        this.newMessages = 0
      }
    },
    resumeAutoScrollToBottom() {
      this.autoScroll = true
      this.newMessages = 0
      const self = this
      this.$nextTick(function () {
        window.setTimeout(function () {
          self.scrollToBottom()
        }, 50)
      })
    },
    scroll() {
      if (this.autoScroll) {
        window.setTimeout(() => this.scrollToBottom(), 100)
      } else {
        this.newMessages++
      }
    },
    scrollToBottom() {
      const el = this.$refs.bottomOfMessages
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }
}
</script>
<style lang="scss">
#chat-session {
  flex: 1;
  margin: auto;
  display: flex;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  max-width: 1200px;

  flex-direction: column;
  align-items: center;

  .chat-messages {
    padding: 20px 10px 0 10px;
    flex: 1;
    max-width: 1200px;
    width: 100%;
  }

  .message-creating {
    height: initial;
    width: initial;

    &.user-creating {
      float: right
    }

    &.system-creating {
      float: left
    }
  }


  .chat-controls {
    width: 100%;
    padding: 10px 120px;
    margin: 10px;
  }

  .notification-bar {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 6px;
    font-size: 0.8em;
    margin-top: 10px;

    .ws-status {
      padding: 4px 10px;
      border-radius: 999px;
      background: #f3f5f8;
      color: #64748b;
      border: 1px solid #e7ecf2;

      &.disconnected {
        color: #9a3412;
        background: #fff7ed;
        border-color: #fed7aa;
      }

      &.connecting {
        color: #1e3a5f;
        background: #eff6ff;
        border-color: #bfdbfe;
      }
    }
  }

  .message {
    &.system {
      justify-items: flex-start;
    }

    &.user {
      justify-items: flex-end;
    }
  }

  #bottom {
    height: 10px;
  }
}

:root {
  --user-message-color: #333;
  --user-message-background: #ececec;
  --table-header-background: var(--user-message-background);
  --table-header-color: var(--user-message-color);
  --session-list-background: var(--user-message-background);
  --session-list-group-header-color: #333;
  --session-list-link: #444;
  --session-list-time-ago: var(--session-list-link);
}
</style>
